import {
  UserCircle2,
  SearchCheck,
  SendHorizonal,
  PartyPopper,
  ArrowRight,
} from "lucide-react";
import { motion } from "motion/react";
import { Link } from "react-router-dom";

const steps = [
  {
    step: "01",
    icon: <UserCircle2 className="w-6 h-6" />,
    title: "Create One Profile",
    desc: "Fill out a single smart profile in under 5 minutes. Your grades, goals, and preferences — captured once.",
    color: "bg-violet-600",
    glow: "shadow-violet-500/30",
    light: "bg-violet-50",
    textColor: "text-violet-600",
    borderColor: "border-violet-200",
  },
  {
    step: "02",
    icon: <SearchCheck className="w-6 h-6" />,
    title: "We Match You",
    desc: "Our system cross-references your profile against 300+ universities and scholarships to find your best fits.",
    color: "bg-cyan-500",
    glow: "shadow-cyan-500/30",
    light: "bg-cyan-50",
    textColor: "text-cyan-600",
    borderColor: "border-cyan-200",
  },
  {
    step: "03",
    icon: <SendHorizonal className="w-6 h-6" />,
    title: "Apply to Many — Free",
    desc: "One click sends you to multiple institutions. No redundant forms. No application fees. We handle the paperwork.",
    color: "bg-orange-500",
    glow: "shadow-orange-500/30",
    light: "bg-orange-50",
    textColor: "text-orange-600",
    borderColor: "border-orange-200",
  },
  {
    step: "04",
    icon: <PartyPopper className="w-6 h-6" />,
    title: "Get Your Offers",
    desc: "Receive multiple offer letters and compare side-by-side. We help you negotiate and choose the best deal.",
    color: "bg-lime-500",
    glow: "shadow-lime-500/30",
    light: "bg-lime-50",
    textColor: "text-lime-600",
    borderColor: "border-lime-200",
  },
];

const perks = [
  { label: "Zero Application Fees", sub: "100% free, always" },
  { label: "Multiple Offers", sub: "Compare side by side" },
  { label: "Dedicated Counselor", sub: "Your personal guide" },
];

export function ProcessSection() {
  return (
    <section
      className="py-24 lg:py-32 bg-white relative overflow-hidden"
      id="process"
    >
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent" />
        <svg
          className="absolute right-0 top-0 opacity-[0.035] w-[500px] h-[500px]"
          viewBox="0 0 500 500"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#06B6D4"
            d="M421.5,298Q398,346,360.5,383.5Q323,421,268,424Q213,427,169,402Q125,377,97,333Q69,289,70.5,236Q72,183,103.5,143Q135,103,184.5,82.5Q234,62,283.5,62Q333,62,376,88Q419,114,432,162Q445,210,421.5,298Z"
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 mb-6">
            <span
              className="text-[12px] text-slate-600 uppercase tracking-widest"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 700 }}
            >
              The Process
            </span>
          </div>
          <h2
            className="text-[40px] lg:text-[52px] text-slate-900 tracking-tight mb-4"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              lineHeight: 1.1,
            }}
          >
            One profile.{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #06B6D4, #7C3AED)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Unlimited doors.
            </span>
          </h2>
          <p
            className="text-[16px] text-slate-500 max-w-xl mx-auto"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
          >
            Stop applying one-by-one and getting buried in forms. Our
            streamlined platform makes multi-university applications feel like
            sending a single message.
          </p>
        </motion.div>

        {/* Perks row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-wrap justify-center gap-3 mb-20"
        >
          {perks.map((perk, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-slate-50 border border-slate-200"
            >
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-500" />
              <div className="flex flex-col leading-none">
                <span
                  className="text-[13px] text-slate-800"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
                >
                  {perk.label}
                </span>
                <span
                  className="text-[11px] text-slate-400"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                >
                  {perk.sub}
                </span>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Steps */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative group"
            >
              {/* Connector arrow — desktop */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-10 -right-3 z-10">
                  <ArrowRight className="w-5 h-5 text-slate-200" />
                </div>
              )}

              <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-slate-900/5 transition-all duration-500 group-hover:-translate-y-1 h-full flex flex-col">
                {/* Step number */}
                <div className="flex items-center justify-between mb-5">
                  <div
                    className={`w-12 h-12 rounded-2xl ${step.color} ${step.glow} shadow-lg flex items-center justify-center text-white`}
                  >
                    {step.icon}
                  </div>
                  <span
                    className="text-[40px] leading-none text-slate-100 select-none"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 900 }}
                  >
                    {step.step}
                  </span>
                </div>

                <h4
                  className="text-[16px] text-slate-900 mb-2"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 700 }}
                >
                  {step.title}
                </h4>
                <p
                  className="text-[13px] text-slate-500 leading-relaxed flex-1"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                >
                  {step.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden bg-slate-900 p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-8"
        >
          {/* BG glow */}
          <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-violet-600/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />

          <div className="relative z-10 text-center lg:text-left">
            <h3
              className="text-[28px] lg:text-[36px] text-white mb-3 tracking-tight"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                lineHeight: 1.1,
              }}
            >
              Ready to apply smarter,{" "}
              <span
                style={{
                  background: "linear-gradient(90deg, #A78BFA, #67E8F9)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                not harder?
              </span>
            </h3>
            <p
              className="text-[14px] text-slate-400 max-w-md"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
            >
              Join 4,000+ students who've used SCS Eduction to secure their
              future — for free.
            </p>
          </div>

          <Link
            to={"/register"}
            className="relative z-10 flex-shrink-0 px-8 py-4 rounded-2xl bg-gradient-to-r from-violet-500 to-cyan-500 text-white flex items-center gap-2 shadow-xl shadow-violet-500/30 hover:shadow-violet-500/50 hover:-translate-y-0.5 transition-all duration-200"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 700,
              fontSize: "15px",
            }}
          >
            Create My Free Profile <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
